import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  addItemToLocalStorage,
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
} from 'utils/functions/storage';
import { registerFormMixpanelHandler } from 'utils/functions/mixpanel';
import { fetchRegisterFormSubmission } from 'modules/general/actions';
import { registerFormStepsEnum, registerFormValidationErrorType } from 'modules/general/enums';
import RegisterComponent from 'modules/general/components/RegisterComponent';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
const requiredFormFieldsNameToStepNumberMap = {
  [registerFormStepsEnum.businessIndustry]: ['businessIndustry', 'otherbusinessIndustry'],
  [registerFormStepsEnum.companyInformation]: [
    'companyName',
    'companyCity',
    'companyArea',
    'companyLocation',
    'companyApartment',
    'companyEmail',
    'companyMobileNumber',
  ],
  [registerFormStepsEnum.userPosition]: ['userPosition', 'otheruserPosition'],
  [registerFormStepsEnum.accountInformation]: [
    'accountName',
    'accountEmail',
    'accountMobileNumber',
    'accountPassword',
  ],
};

const requiredFormFieldsWithRegexValidation = {
  companyEmail: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i,
  companyMobileNumber: /^01[0-9]{9}$/i,
  accountEmail: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i,
  accountMobileNumber: /^01[0-9]{9}$/i,
  accountPassword: /^.{6,}$/i,
};

const optionalFormFieldsWithRegexValidation = {
  companyWebsite:
    /^(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/i,
};
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const RegisterContainer = ({ mixpanel }) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const dispatch = useDispatch();
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [currentStep, setCurrentStep] = useState(registerFormStepsEnum.businessIndustry);
  const [formFieldsValues, setFormFieldsValues] = useState({
    businessIndustry: '',
    otherbusinessIndustry: true,
    companyName: '',
    companyCity: '',
    companyArea: '',
    companyLocation: '',
    companyApartment: '',
    companyEmail: '',
    companyWebsite: '',
    companyMobileNumber: '',
    companyPhoneNumber: '',
    companyFax: '',
    userPosition: '',
    otheruserPosition: true,
    accountName: '',
    accountEmail: '',
    accountMobileNumber: '',
    accountPassword: '',
    companyCityName: '',
    companyAreaName: '',
  });
  const [formFieldsValidation, setFormFieldsValidation] = useState({
    businessIndustry: registerFormValidationErrorType.valid,
    otherBusinessIndustry: registerFormValidationErrorType.valid,
    companyName: registerFormValidationErrorType.valid,
    companyCity: registerFormValidationErrorType.valid,
    companyArea: registerFormValidationErrorType.valid,
    companyLocation: registerFormValidationErrorType.valid,
    companyApartment: registerFormValidationErrorType.valid,
    companyEmail: registerFormValidationErrorType.valid,
    companyMobileNumber: registerFormValidationErrorType.valid,
    userPosition: registerFormValidationErrorType.valid,
    accountName: registerFormValidationErrorType.valid,
    accountEmail: registerFormValidationErrorType.valid,
    accountMobileNumber: registerFormValidationErrorType.valid,
    accountPassword: registerFormValidationErrorType.valid,
  });
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  useEffect(() => {
    const savedFromData = getItemFromLocalStorage('formData');
    if (savedFromData) {
      const parsedSavedFormData = JSON.parse(savedFromData);

      setFormFieldsValues(parsedSavedFormData);
    }
  }, [currentStep]);
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  const submitRegisterFormSuccessCallback = () => {
    setCurrentStep(registerFormStepsEnum.confirmation);

    registerFormMixpanelHandler.fireRegisterFormMixpanelEvent(
      registerFormStepsEnum.confirmation,
      mixpanel,
      formFieldsValues,
    );
  };

  const submitRegisterFormFailCallback = () => {
    setCurrentStep(registerFormStepsEnum.error);
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleTextInputFieldChange = (fieldName, fieldEvent) => {
    const { value } = fieldEvent.target;

    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = value;

      return fieldsObj;
    });
  };

  const handleDropdownFieldChange = (fieldName, fieldValue) => {
    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = fieldValue.key;
      fieldsObj[`${fieldName}Name`] = fieldValue.name;

      if (fieldName === 'companyCity') {
        fieldsObj.companyArea = '';
        fieldsObj.companyAreaName = '';
      }

      return fieldsObj;
    });
  };

  const handleRadioBtnFieldChange = (fieldName, fieldValue) => {
    setFormFieldsValues(prevState => {
      const fieldsObj = { ...prevState };
      fieldsObj[fieldName] = fieldValue.key;
      if (fieldValue.key === 'Other') fieldsObj[`other${fieldName}`] = '';
      else {
        fieldsObj[`other${fieldName}`] = true;
        setFormFieldsValidation(prevStateValue => ({
          ...prevStateValue,
          [`other${fieldName}`]: registerFormValidationErrorType.valid,
        }));
      }

      return fieldsObj;
    });
  };

  const handleNextBtnClick = () => {
    let isFormValid = true;

    // we get all required fields for the current step and then make sure it has a value
    // and if it has a special regex requirement we validate it against that regex
    const currentStepRequiredFieldsArr = requiredFormFieldsNameToStepNumberMap[currentStep];
    currentStepRequiredFieldsArr.forEach(fieldName => {
      let isFieldValid = registerFormValidationErrorType.valid;

      if (
        !formFieldsValues[fieldName] ||
        (requiredFormFieldsWithRegexValidation[fieldName] &&
          requiredFormFieldsWithRegexValidation[fieldName].test(formFieldsValues[fieldName]) ===
            false)
      ) {
        isFieldValid =
          (!formFieldsValues[fieldName] && registerFormValidationErrorType.empty) ||
          registerFormValidationErrorType.wrong;
        isFormValid = false;
      }

      setFormFieldsValidation(prevState => ({
        ...prevState,
        [fieldName]: isFieldValid,
      }));
    });

    // we get all optional fields that require specific regix if value is entered
    // and we validate it against that regex
    Object.keys(optionalFormFieldsWithRegexValidation).forEach(key => {
      const fieldValue = formFieldsValues[key];

      let isFieldValid = registerFormValidationErrorType.valid;
      if (fieldValue && optionalFormFieldsWithRegexValidation[key].test(fieldValue) === false) {
        isFieldValid = registerFormValidationErrorType.wrong;
        isFormValid = false;
      }

      setFormFieldsValidation(prevState => ({
        ...prevState,
        [key]: isFieldValid,
      }));
    });

    if (isFormValid === false) return;

    // save user data to local storage
    addItemToLocalStorage('formData', JSON.stringify(formFieldsValues));

    registerFormMixpanelHandler.fireRegisterFormMixpanelEvent(
      currentStep,
      mixpanel,
      formFieldsValues,
    );

    if (currentStep !== registerFormStepsEnum.accountInformation) {
      setCurrentStep(prevState => prevState + 1);
      return;
    }

    // clear saved data since we are about to submit data to API
    removeItemFromLocalStorage('formData');

    const body = {
      data: {
        industry:
          (formFieldsValues.businessIndustry === 'Other' &&
            `Other - ${formFieldsValues.otherbusinessIndustry}`) ||
          formFieldsValues.businessIndustry,
        BusinessName: formFieldsValues.companyName,
        cityKey: formFieldsValues.companyCity,
        areaKey: formFieldsValues.companyArea,
        address: formFieldsValues.companyLocation,
        apartment: formFieldsValues.companyApartment,
        email: formFieldsValues.companyEmail,
        webUrl: formFieldsValues.companyWebsite,
        mobileNumber: formFieldsValues.companyMobileNumber,
        phoneNumber: formFieldsValues.companyPhoneNumber,
        fax: formFieldsValues.companyFax,
        userRequestModel: {
          fullName: formFieldsValues.accountName,
          email: formFieldsValues.accountEmail,
          phone: formFieldsValues.accountMobileNumber,
          password: formFieldsValues.accountPassword,
          positionKey:
            (formFieldsValues.userPosition === 'Other' &&
              `Other - ${formFieldsValues.otheruserPosition}`) ||
            formFieldsValues.userPosition,
        },
      },
      callbackActionSuccess: submitRegisterFormSuccessCallback,
      callbackActionFail: submitRegisterFormFailCallback,
    };

    dispatch(fetchRegisterFormSubmission(body));
  };

  const handleBackBtnClick = () => {
    // when the user click the back button we need to reset all validations
    setFormFieldsValidation(prevState => {
      const updatedState = {};
      Object.keys(prevState).forEach(key => {
        updatedState[key] = registerFormValidationErrorType.valid;
      });

      return updatedState;
    });
    setCurrentStep(prevState => prevState - 1);
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <RegisterComponent
      currentStep={currentStep}
      formFieldsValues={formFieldsValues}
      formFieldsValidation={formFieldsValidation}
      handleTextInputFieldChange={handleTextInputFieldChange}
      handleDropdownFieldChange={handleDropdownFieldChange}
      handleRadioBtnFieldChange={handleRadioBtnFieldChange}
      handleNextBtnClick={handleNextBtnClick}
      handleBackBtnClick={handleBackBtnClick}
    />
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

RegisterContainer.propTypes = {
  mixpanel: PropTypes.shape({}).isRequired,
};

RegisterContainer.defaultProps = {};

export default RegisterContainer;
